import { TabItemsType } from 'components/landing-page-wrapper/components/TabComponent';
import { EmptyPipelineIcon } from '../../../assets';
import { themeObject, validationsMessages } from 'lib';
import * as yup from 'yup';

export const DETAILS = 'Details';
export const STEPS = 'Steps';

export type TabsItemsType = typeof DETAILS | typeof STEPS;

export interface CreatePipelineFormData {
    id: string;
    name: string;
    description: string;
    stepIds: string[];
}

export const CreatePipelineSchema = yup.object({
    name: yup.string().required(validationsMessages.required),
    description: yup.string().optional(),
});

export const CreatePipelineDefaults = {
    id: '',
    name: '',
    description: '',
    stepIds: [],
};

export const createPipelineConfig: {
    [key: string]: {
        imgSrc: string;
        headingText?: string;
        iconStyles?: React.CSSProperties;
    };
} = {
    pipeline: {
        imgSrc: EmptyPipelineIcon,
        headingText: 'New Pipeline',
        iconStyles: {
            display: 'none',
        },
    },
};

export interface RemoveMenuItemProps {
    index: number;
    handleRemoveStep: (index: number) => void;
}

export const createPipelineStyles = {
    root: {
        '& .MuiDialog-container': {
            backgroundColor: 'rgb(249,251,251)',
        },
        '& .MuiPaper-root': {
            boxShadow: 'none',
            maxWidth: '864px',
            maxHeight: 'none',
            height: '100%',
            overflow: 'auto',
            '#dialog-button-container.MuiBox-root': {
                zIndex: 10,
                '& .MuiIconButton-root': {
                    top: '69px',
                    right: '68px',
                    mt: 0,
                    width: '34px',
                    height: '34px',
                    p: '6px',
                    '& .MuiSvgIcon-root': {
                        height: '22px',
                        width: '22px',
                    },
                },
            },
        },
    },
};

export const DETAILS_TAB_INDEX = 0;
export const STEPS_TAB_INDEX = 1;

export const TabItems: TabItemsType[] = [
    {
        label: DETAILS,
        tabHeaderConfig: {
            headingText: 'Pipeline Details',
        },
    },
    {
        label: STEPS,
        tabHeaderConfig: {
            headingText: 'Pipeline Steps',
        },
    },
];

export const updatePipelineStyles = {
    containerStyles: {
        display: 'flex',
        flexDirection: 'row',
    },
    headingTextStyles: {
        ...themeObject.typography.h3,
        color: themeObject.palette.primary[700],
    },
    contentContainerStyles: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        border: `1px solid ${themeObject.palette.grayScale[300]}`,
        backgroundColor: themeObject.palette.background.default,
        p: '32px 24px',
        borderRadius: '5px',
    },
    emptyContainerStyles: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        gap: 4,
    },
    emptyStepsImageStyles: {
        width: '106px',
        height: '112px',
    },
    badgeStyles: {
        top: '-15px',
        right: '-30px',
        '& .MuiBadge-badge': {
            backgroundColor: themeObject.palette.tertiary.red.A100_1,
        },
    },
    modalStyles: {
        '#dialog-button-container': {
            '& .MuiIconButton-colorGrayalt': {
                mt: '30px !important',
            },
        },
        '& .MuiDialogTitle-root': {
            p: '31.5px 35px 20px !important',
        },
    },
    draggableListContainerStyles: {
        overflow: 'auto',
        mt: 1.25,
        px: 1,
        pb: 0.5,
    },
    draggableListStyles: {
        '& .MuiGrid-root': {
            '& > .MuiGrid-root:nth-of-type(3)': {
                ml: 0,
                '& .MuiButtonBase-root': {
                    marginLeft: '38.5px',
                },
            },
        },
        '& .MuiPopper-root': {
            '& li': {
                height: '55px',
            },
            '& .MuiGrid-root': {
                '& .MuiGrid-root: last-child': {
                    py: 1,
                },
            },
        },
    },
    draggableItemContainerStyles: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 2,
        width: '100%',
    },
    draggableItemStyles: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'baseline',
        gap: 1,
    },
    draggableItemLabelStyles: {
        ...themeObject.typography.body.default,
        ...themeObject.typography.body.body1,
        ...themeObject.typography.body.semibold,
    },
    draggableItemValueStyles: {
        ...themeObject.typography.body.default,
        ...themeObject.typography.body.body1,
        ...themeObject.typography.body.regular,
    },
    menuItemContainer: {
        p: '14px 0 14px 16px',
    },
    menuItem: {
        display: 'flex',
        flexDirection: 'row',
        gap: 1,
    },
    draggableMenuItemStyles: {
        fontSize: '18px',
    },
    removeStepIconStyles: {
        height: '24px',
        width: '24px',
        color: themeObject.palette.tertiary.red[300],
    },
    addStepHeaderButtonWidth: {
        width: '131px',
    },
    trialRunHeaderButtonWidth: {
        width: '137px',
    },
    tooltipOverflow: {
        maxHeight: '50vh',
        overflowY: 'auto',
    },
};

export const unsavedChangesConfirmationText =
    'This pipeline has unsaved changes. Are you sure you want to close without saving? You will lose all changes.';

export const checkForDuplicatePipeline = (
    pipelines: any[],
    pipeline: any
): boolean => {
    return pipelines.some(
        (p: any) => p.name === pipeline.name && p.id !== pipeline.id
    );
};

export interface ListItemType {
    id: string | number;
    item: string | React.JSX.Element;
}

type StepFieldKey = 'name' | 'type' | 'description';

export const STEP_FIELDS: {
    key: StepFieldKey;
    label: string;
    width: string;
    tooltip?: boolean;
}[] = [
    { key: 'name', label: 'Name', width: '30%', tooltip: true },
    { key: 'type', label: 'Type', width: '30%' },
    { key: 'description', label: 'Description', width: '40%', tooltip: true },
];
